import {
    CreateUpdateBookingMeetingDetails,
    CreateUpdateBookingResourceTableSetups,
} from "../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import { OrderType } from "../../../modules/payments/orderTypes"
import { MeetingServiceOrderDetails } from "../dialogs/receiptConfirmOrderDialog/receiptConfirmOrderDialogTypes"
import { ProductsTableItem } from "../../../modules/merchant/admin/products/productsTypes"
import { OutlookSessionOrder } from "../../../modules/order/pages/outlookCreateOrderTypes"
import { MonetaryAmount } from "../../../modules/currency/currencyTypes"
import { DynamicConfigKey } from "./components/form/formTypes"
import { OrganizerType } from "../../types/OrganizerTypes"
import { UserType } from "../../../modules/login/loginTypes"

export enum CreateOrderDialogType {
    ORDER = "order",
    CREDIT_NOTE = "credit-note",
    SUPPLEMENTARY_ORDER = "supplementary-order",
}

export type CreateOrderDialogMode = "create" | "edit" | "duplicate"
export enum CreateOrderFlowType {
    OUTLOOK = "outlook",
}

export enum CreateOrderOutlookFlowType {
    WEB = "outlook-web",
    VSTO = "outlook-vsto",
}

export enum CreateOrderDialogFlowMode {
    MEETING_SERVICE = "meetingService",
    RESOURCE_BOOKING = "resourceBooking",
}

export type CreateOrderDialogFlow = {
    flow: CreateOrderFlowType.OUTLOOK | string | null
    mode: CreateOrderDialogFlowMode | null
    vstoFlowParam?: string
    order?: OutlookSessionOrder
    calendarAppointmentId?: string
}

export type ServiceCenterTimeline = Readonly<{
    id: number
    uid: string
    name: string
    resources: ServiceCenterTimelineResource[]
    address: AddressInfo
    floorPlan?: FloorPlanInfo
    messaging?: MessagingInfo
}>

type AddressInfo = Readonly<{
    street: string
    streetNumber: string
    postalCode: string
    city: string
}>

type FloorPlanInfo = Readonly<{
    imageUrl: string
}>

type MessagingInfo = Readonly<{
    enabled: boolean
}>

export type ServiceCenterTimelineResource = Readonly<{
    id: number
    name: string
    displayName: string
    type: ResourceType

    colorCode?: string
    buffer?: number
    outOfService?: OutOfServiceResource
    accessControl?: ResourceAccessControl
    image?: {
        url: string
    }
    room: ResourceRoom
}>
export type ResourceRoom = Readonly<{
    avEquipments?: TimelineResourceFixedAvEquipment[]
    floor?: {
        name: string
        value: number
    }
    meetingUsage?: {
        internal: boolean
        external: boolean
    }
    seats?: number
    tableSetups?: ResourceTableSetups[]
}>

export type TimelineResourceFixedAvEquipment = Readonly<{
    id: number
    name: string
}>

export type OutOfServiceResource = Readonly<{
    active: boolean
    message: string
}>

export type ResourceTableSetups = Readonly<{
    key: string
    name: string
    imageUrl: string
}>

export type ResourceAccessControl = Readonly<{
    isLimited: boolean
    hasAccess: boolean
}>

export type ResourceType = Readonly<{
    id: number
    name: string
}>

export type EventsForServiceCenter = Readonly<{
    orderId: number
    resourceIds: number[]
    startDateTime: string
    endDateTime: string
    subject?: string
    canEdit?: boolean
    outOfService?: boolean
    host?: string
}>

// FILTERS
export type ResourceFilterElementValue = Readonly<{
    key: string | number
    value: string | number
    imageUrl?: string
}>

export type ResourceFilterElement = Readonly<{
    elementType:
        | "MultiSelect"
        | "CheckBox"
        //     | "ComboBox"
        //     | "DropDown"
        | "PlusMinusButton"
        | "TimeSlider"
        | "DateTimePicker"
        | "Switcher"
        | string
    identifier: string
    name: string
    translationKey: string
    values: ResourceFilterElementValue[]
}>

export type ResourceFilterType = Readonly<{
    filterElements: ResourceFilterElement[]
    name: string
    translationKey: string
}>

export type ResourceFiltersTypes = Readonly<{
    filters: ResourceFiltersSelectedItem[]
    search: string
    isBookedPeriod?: boolean
}>

export type ResourceFiltersSelectedItem = Readonly<{
    identifier: string
    isRemovable?: boolean
    values: ResourceFilterElementValue[]
}>

export type CreateUpdateBookingOrganizer = Readonly<{
    id?: number
    organizerType: OrganizerType
    name?: string
    email?: string
}>

export type CreateUpdateBooking = Readonly<{
    order?: { id: number }
    orderType: OrderType
    organizers: CreateUpdateBookingOrganizer[]
    meetingDetails: CreateUpdateBookingMeetingDetails
    messageToServiceCenter?: string
    bookingDetails: {
        resources: CreateUpdateBookingResourceTableSetups[]
    }
}>

export type CreateUpdateBookingOrder = Readonly<{
    id: number
    uid: string
    bookingDetails?: {
        resources: CreateUpdateBookingResourceTableSetups[]
    }
    orderType: OrderType
    meetingDetails: CreateUpdateBookingMeetingDetails
    organizers: CreateUpdateBookingOrganizer[]
    messageToServiceCenter?: string
}>
export enum CalendarImportLinkRel {
    ICS_APPOINTMENT = "ICS_APPOINTMENT",
    ICS_MEETING_REQUEST = "ICS_MEETING_REQUEST",
}
export type CreateUpdateBookingCalendarImportLinks = Readonly<{
    rel: CalendarImportLinkRel
    href: string
    translationKey: string
}>

//  Create / update Meeting Service Order

export type CreateUpdateMeetingServiceOrder = MeetingServiceOrderDetails & {
    calendarAppointmentId?: string
}

// Free / Busy Schedule
export type AvailabilityDetails = Readonly<{
    startTime: string
    endTime: string
    availabilityViewInterval: number
}>
export type ResourceScheduleItem = Readonly<{
    resource: ResourceDetails
    availability: AvailabilityItem[]
}>
export type ResourceDetails = Readonly<{
    id: number
    uuid: string
    resourceNo: string
    name: string
}>

export type AvailabilityItem = Readonly<{
    startTime: string
    endTime: string
    availabilityView: string // 0 = free, 2 = busy
}>

type BookingOrderMeetingDetails = Readonly<{
    meetingName: string
    location: string
    meetingTimeAsText: string
    startDateTime: string
    endDateTime: string
    meetingDuration: number
}>
type BookingOrderOrganizer = Readonly<{
    organizerType: string
    id: number
    uid: string
    name?: string
    email?: string
    businessPhone?: string
    mobilePhone?: string
    company?: {
        id: number
        name: string
    }
}>

export type BookingOrderDetailsItem = Readonly<{
    id: number
    uid: string
    meetingDetails: BookingOrderMeetingDetails
    organizers: BookingOrderOrganizer[]
}>

// FAVORITES

export type SaveProductFavoriteBody = Readonly<{
    id: number
    productType: string // MEETING_SERVICE
}>
export type ProductFavorite = Readonly<{
    id: number
    userDetails: {
        favoriteType: string
        favoriteId: number
        canFavorite: boolean
    }
    productType: string
}>

export type ProductWithQuantity = ProductsTableItem & {
    quantity: number
    isExtraService?: boolean
    type?: DynamicConfigKey.PRODUCT_WISH
    priceListId?: number
    itemPrice?: MonetaryAmount
}

export type SelectedPriceListItem = Readonly<{
    merchantId: number
    merchantName: string
    priceListId: number
    priceListName: string
    shopId: number
    isOnlyOnePriceList: boolean
}>

export type ResourceItem = Readonly<{
    id: number
    title: string
    // hasMessagingEnabled?: boolean
    tableSetups?: ResourceTableSetups[]
    type: ResourceType
}>

export type SelectedMainUserState = Readonly<{
    userId: number
    displayName: string
    companyId: number
    userType: UserType
}>

export type ExtraServiceDetails = Readonly<{
    id: number
    type: "PER_SEAT" | string
    name: string
    description: string
    productGroupCode: string
    minimumPrice: MonetaryAmount
    maximumPrice: MonetaryAmount
    pricePerSeat: MonetaryAmount
    isMandatory: boolean
    isActive: boolean
}>

export enum MessageSeverity {
    ERROR = "ERROR",
    WARNING = "WARNING",
    INFO = "INFO",
    SUCCESS = "SUCCESS",
}
export type Message = Readonly<{
    id: number
    severity: MessageSeverity
    title: string
    publishDate: string //"2024-01-04T11:23:22"
}>

export type OrganizerSelectedValue = Readonly<{
    id?: number
    name?: string
    email?: string
}>

export type BillReceiversItemMS = Readonly<{
    id: number
    type: string
    vatnumber: string
    name: string
    address: BillReceiverAddressMS
    endpoint: EndpointEan | EndpointEmail
    contactDetails?: BillReceiversItemContactDetails
}>

type BillReceiverAddressMS = Readonly<{
    displayName: string
}>
export enum BillReceiverTypeMS {
    EAN_NUMBER = "EAN",
    EMAIL_ADDRESS = "EMAIL",
}
export type EndpointEan = Readonly<{
    type: BillReceiverTypeMS.EAN_NUMBER
    ean: string
}>
export type EndpointEmail = Readonly<{
    type: BillReceiverTypeMS.EMAIL_ADDRESS
    email: string
}>

export type BillReceiversItemContactDetails = Readonly<{
    name: string
    email?: string
    phoneNumber?: string
}>

export enum SatelliteType {
    EAT_AT_LOCATION = "EAT_AT_LOCATION",
    PICKUP_LOCATION = "PICKUP_LOCATION",
    DELIVERY_LOCATION = "DELIVERY_LOCATION",
}
export type SatelliteDetails = Readonly<{
    id: number
    types: ReadonlyArray<SatelliteType>
    name: string
    description: string
}>

export type AccountDimensionDetailsForUser = Readonly<{
    id: number
    name: string
    costCenters: AccountDimensionCostCenter[]
}>

type AccountDimensionCostCenter = Readonly<{
    value: string
    name: string
}>
